import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { cn } from '../../utils/cn';

// Single always-visible "..." trigger per row, opening a dropdown of that
// row's actions — replaces the old opacity-0 group-hover:opacity-100 icon
// row, which was invisible by default on any touch device (no hover state)
// and, once visible, easy to lose behind a horizontally-scrolled table.
//
// The dropdown is rendered into a portal on document.body instead of as a
// normal absolutely-positioned child. Table rows here sit in `position:
// sticky` cells (for the pinned Actions column), and sticky elements each
// create their own stacking context — a later row's sticky cell paints over
// an earlier row's open dropdown no matter how high its z-index is, because
// z-index only competes within the same stacking context. Portaling escapes
// the table entirely, so the menu always renders above everything.
//
// `actions`: [{ label, icon: LucideIcon, onClick, danger, disabled, hidden }]
// Items with `hidden: true` are skipped entirely (for status-gated actions).
export default function RowActionsMenu({ actions, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const computePosition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 200;
    const left = align === 'right'
      ? Math.max(8, rect.right - menuWidth)
      : rect.left;
    setCoords({ top: rect.bottom + 4, left: Math.min(left, window.innerWidth - menuWidth - 8) });
  }, [align]);

  useLayoutEffect(() => {
    if (!open) return;
    computePosition();

    const onDocClick = e => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    const onReposition = () => computePosition();

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, computePosition]);

  const visibleActions = (actions || []).filter(a => !a.hidden);
  if (visibleActions.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        title="Actions"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors"
      >
        <MoreVertical size={16} />
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: 200 }}
          className="z-[1000] bg-white border border-[#E5E5E5] rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] py-1"
        >
          {visibleActions.map((action, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={action.disabled}
              onClick={e => { e.stopPropagation(); setOpen(false); action.onClick(); }}
              className={cn(
                'w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] font-medium text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
                action.danger ? 'text-[#BA0517] hover:bg-[#FDE7E9]' : 'text-[#080707] hover:bg-[#F3F2F2]'
              )}
            >
              {action.icon && <action.icon size={14} className="shrink-0" />}
              {action.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
